import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { programmeAPI, domainesExpertiseAPI } from '../api/api';

const STATUT_CONFIG = {
  a_faire:  { label: 'À faire',   className: 'bg-gray-100 text-gray-600' },
  en_cours: { label: 'En cours',  className: 'bg-orange-100 text-orange-700' },
  fait:     { label: 'Fait',      className: 'bg-green-100 text-green-700' },
};

const STATUT_CYCLE = ['a_faire', 'en_cours', 'fait'];


// ── Cellule éditable inline ───────────────────────────────────────────────────
function EditableCell({ value, onSave, placeholder = '', type = 'text', className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);
  useEffect(() => { setDraft(value ?? ''); }, [value]);

  const commit = () => {
    setEditing(false);
    const normalized = type === 'number' ? (draft === '' ? null : parseInt(draft, 10)) : draft;
    if (normalized !== (value ?? '')) onSave(normalized);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); } }}
        className={`w-full px-1 py-0.5 border border-blue-400 rounded text-sm focus:outline-none ${className}`}
        style={{ color: '#111827' }}
        placeholder={placeholder}
      />
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className={`block w-full px-1 py-0.5 cursor-text rounded hover:bg-blue-50 text-sm min-h-[1.5rem] ${!value && !draft ? 'text-gray-300' : ''} ${className}`}
      title="Cliquer pour modifier"
    >
      {draft || <span className="text-gray-300">{placeholder}</span>}
    </span>
  );
}

// ── Cellule commentaire (contenteditable — pas de layout shift) ───────────────
function CommentCell({ value, onSave, placeholder = '' }) {
  const ref = useRef(null);
  const savedValue = useRef(value ?? '');

  useEffect(() => {
    savedValue.current = value ?? '';
    if (ref.current && ref.current !== document.activeElement) {
      ref.current.innerText = value ?? '';
    }
  }, [value]);

  const handleBlur = () => {
    const trimmed = (ref.current?.innerText ?? '').trim();
    if (trimmed !== savedValue.current) onSave(trimmed || null);
  };

  return (
    <span
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={e => { if (e.key === 'Escape') { ref.current.innerText = savedValue.current; ref.current.blur(); } }}
      data-placeholder={placeholder}
      className="block w-full px-1 py-0.5 rounded text-sm min-h-[1.5rem] cursor-text hover:bg-blue-50 focus:outline-none focus:border focus:border-blue-400 focus:bg-white whitespace-pre-wrap"
    >
      {value ?? ''}
    </span>
  );
}

// ── Ligne activité ────────────────────────────────────────────────────────────
function ActiviteRow({ activite, index, onUpdate, onDelete, onAddBelow }) {
  const rowBg = activite.statut === 'fait'     ? 'bg-green-50'
              : activite.statut === 'en_cours' ? 'bg-orange-50'
              : 'bg-gray-50';
  const statut = STATUT_CONFIG[activite.statut] || STATUT_CONFIG.a_faire;

  const cycleStatut = () => {
    const idx = STATUT_CYCLE.indexOf(activite.statut);
    onUpdate({ statut: STATUT_CYCLE[(idx + 1) % STATUT_CYCLE.length] });
  };

  return (
    <tr className={`${rowBg} border-b border-gray-100 group`}>
      <td className="w-24 px-1">
        <div className="flex items-center pl-3">
          <span className="text-gray-300 text-xs mr-1 flex-shrink-0">↳</span>
          <span className="text-xs text-purple-500 whitespace-nowrap">Activité {index}</span>
        </div>
      </td>

      <td className="px-2 py-0.5 pl-6">
        <EditableCell
          value={activite.titre}
          onSave={v => onUpdate({ titre: v })}
          placeholder="Titre de l'activité"
          className="text-gray-600 text-xs"
        />
      </td>

      <td className="w-20 px-2 py-0.5 text-right">
        <EditableCell
          value={activite.duree_minutes != null ? String(activite.duree_minutes) : ''}
          onSave={v => onUpdate({ duree_minutes: v })}
          placeholder="—"
          type="number"
          className="text-right text-xs text-gray-500"
        />
      </td>

      <td className="w-24 px-2 py-0.5 text-center">
        <button
          onClick={cycleStatut}
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${statut.className} whitespace-nowrap`}
        >
          {statut.label}
        </button>
      </td>

      <td className="w-32 px-2 py-0.5">
        <EditableCell value={activite.formateur} onSave={v => onUpdate({ formateur: v })} placeholder="—" className="text-xs text-gray-500" />
      </td>

      <td className="px-2 py-0.5">
        <CommentCell value={activite.commentaire} onSave={v => onUpdate({ commentaire: v })} placeholder="—" />
      </td>

      <td className="w-16 px-1 text-right">
        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onAddBelow} className="text-gray-400 hover:text-purple-600 text-xs px-1" title="Ajouter une activité en dessous">+</button>
          <button onClick={() => { if (window.confirm('Supprimer cette activité ?')) onDelete(); }} className="text-gray-400 hover:text-red-600 text-xs px-1">×</button>
        </div>
      </td>
    </tr>
  );
}

// ── Ligne module ──────────────────────────────────────────────────────────────
function ModuleRow({ module, domaines, onUpdate, onDelete, onAddBelow, onAddActivite, onUpdateActivite, onDeleteActivite }) {
  const rowBg = module.statut === 'fait'     ? 'bg-green-50'
              : module.statut === 'en_cours' ? 'bg-orange-50'
              : 'bg-white';
  const statut  = STATUT_CONFIG[module.statut] || STATUT_CONFIG.a_faire;
  const isQuiz  = module.type === 'quiz' || module.type === 'quiz_certifiant';
  const activites = module.activites ?? [];

  const [selectedTitre, setSelectedTitre] = useState(module.titre ?? '');
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => { setSelectedTitre(module.titre ?? ''); }, [module.titre]);

  const activitesTotal = activites.reduce((s, a) => s + (a.duree_minutes || 0), 0);
  const hasActivites = activites.length > 0;

  const cycleStatut = () => {
    const idx = STATUT_CYCLE.indexOf(module.statut);
    onUpdate({ statut: STATUT_CYCLE[(idx + 1) % STATUT_CYCLE.length] });
  };

  return (
    <>
      <tr className={`${rowBg} border-b border-gray-100 group`}>
        {/* Type */}
        <td className="w-16 px-1">
          <select
            value={module.type}
            onChange={e => onUpdate({ type: e.target.value })}
            className={`text-xs border-0 bg-transparent focus:ring-0 py-0 px-0 w-full ${isQuiz ? 'text-blue-600 font-medium' : 'text-gray-400'}`}
          >
            <option value="module">Module</option>
            <option value="quiz">Quiz</option>
            <option value="quiz_certifiant">Quiz cert.</option>
          </select>
        </td>

        {/* Titre = sélection du module (domaine d'expertise) */}
        <td className="px-2 py-1 min-w-[280px]">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              {isQuiz ? (
                <EditableCell
                  value={module.titre}
                  onSave={v => onUpdate({ titre: v })}
                  placeholder="Titre du quiz"
                  className="text-blue-700 font-medium"
                />
              ) : (
                <select
                  value={selectedTitre}
                  onChange={e => {
                    setSelectedTitre(e.target.value);
                    if (e.target.value) onUpdate({ titre: e.target.value });
                  }}
                  className="w-full text-sm border-0 bg-transparent focus:ring-0 py-0.5 px-1 rounded hover:bg-blue-50 cursor-pointer text-gray-800"
                >
                  <option value="">— Choisir un module —</option>
                  {domaines.map(d => <option key={d.id} value={d.titre}>{d.titre}</option>)}
                </select>
              )}
            </div>
            {hasActivites && (
              <span className="text-[10px] text-purple-500 bg-purple-50 px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                {activites.length} activité{ activites.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </td>

        {/* Durée */}
        <td className="w-20 px-2 py-1 text-right">
          {hasActivites ? (
            <span className="block w-full px-1 py-0.5 text-right text-sm text-gray-400 bg-gray-50 rounded cursor-not-allowed" title="Calculé depuis les activités">
              {activitesTotal > 0 ? activitesTotal : '—'}
            </span>
          ) : (
            <EditableCell
              value={module.duree_minutes != null ? String(module.duree_minutes) : ''}
              onSave={v => onUpdate({ duree_minutes: v })}
              placeholder="—"
              type="number"
              className="text-right"
            />
          )}
        </td>

        {/* Statut */}
        <td className="w-24 px-2 py-1 text-center">
          <button
            onClick={cycleStatut}
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${statut.className} whitespace-nowrap`}
            title="Cliquer pour changer"
          >
            {statut.label}
          </button>
        </td>

        {/* Formateur */}
        <td className="w-32 px-2 py-1">
          <EditableCell value={module.formateur} onSave={v => onUpdate({ formateur: v })} placeholder="Formateur" />
        </td>

        {/* Commentaire */}
        <td className="px-2 py-1">
          <CommentCell value={module.commentaire} onSave={v => onUpdate({ commentaire: v })} placeholder="Commentaire" />
        </td>

        {/* Actions */}
        <td className="w-16 px-1 text-right">
          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {hasActivites && (
              <button onClick={() => setCollapsed(c => !c)} className="text-gray-400 hover:text-blue-600 text-xs px-1" title={collapsed ? 'Afficher les activités' : 'Masquer les activités'}>
                {collapsed ? '▸' : '▾'}
              </button>
            )}
            <button onClick={() => onAddActivite(activites.length)} className="text-gray-400 hover:text-purple-600 text-xs px-1" title="Ajouter une activité">⊕</button>
            <button onClick={onAddBelow} className="text-gray-400 hover:text-blue-600 text-xs px-1" title="Ajouter un module en dessous">+</button>
            <button onClick={() => { if (window.confirm('Supprimer ce module ?')) onDelete(); }} className="text-gray-400 hover:text-red-600 text-xs px-1">×</button>
          </div>
        </td>
      </tr>

      {!collapsed && activites.map((activite, idx) => (
        <ActiviteRow
          key={activite.id}
          activite={activite}
          index={idx + 1}
          onUpdate={fields => onUpdateActivite(activite.id, fields)}
          onDelete={() => onDeleteActivite(activite.id)}
          onAddBelow={() => onAddActivite(idx + 1)}
        />
      ))}
    </>
  );
}

// ── Section formation (ex-chapitre) ──────────────────────────────────────────
function FormationSection({ chapitre, domaines, onUpdateChapitre, onDeleteChapitre, onAddModule, onUpdateModule, onDeleteModule, onAddActivite, onUpdateActivite, onDeleteActivite }) {
  const mods      = chapitre.modules ?? [];
  const countFait = mods.filter(m => m.statut === 'fait').length;
  const allFait   = mods.length > 0 && countFait === mods.length;
  const allAFaire = mods.length === 0 || mods.every(m => m.statut === 'a_faire');
  const pct       = mods.length > 0 ? Math.round(countFait / mods.length * 100) : 0;

  const headerStyle = allFait   ? 'bg-green-700 text-white'
                    : allAFaire ? 'bg-gray-400 text-white'
                    : 'bg-orange-500 text-white';

  const totalMinutes = mods.reduce((s, m) => {
    const acts = m.activites ?? [];
    const actTotal = acts.reduce((a, act) => a + (act.duree_minutes || 0), 0);
    return s + (acts.length > 0 ? actTotal : (m.duree_minutes || 0));
  }, 0);

  return (
    <>
      <tr className={`${headerStyle} font-bold`}>
        <td className="w-16 px-2 text-center text-sm">
          {mods.length > 0 && <span className="font-semibold opacity-90">{pct}%</span>}
        </td>

        <td className="px-2 py-2" colSpan={3}>
          <EditableCell
            value={chapitre.titre}
            onSave={v => onUpdateChapitre({ titre: v })}
            placeholder="Titre de la formation"
            className="font-bold text-inherit"
          />
        </td>

        <td className="px-2 text-right text-sm opacity-80">
          {totalMinutes > 0 && `${totalMinutes} min`}
        </td>

        <td colSpan={2} className="px-2">
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => onAddModule()}
              className="text-white text-opacity-80 hover:text-opacity-100 text-xs bg-white bg-opacity-20 hover:bg-opacity-30 px-2 py-0.5 rounded transition-all"
            >+ Module</button>
            <button
              onClick={() => { if (window.confirm('Supprimer cette formation et tous ses modules ?')) onDeleteChapitre(); }}
              className="text-white text-opacity-60 hover:text-opacity-100 text-xs transition-all"
            >×</button>
          </div>
        </td>
      </tr>

      {mods.map((module, idx) => (
        <ModuleRow
          key={module.id}
          module={module}
          domaines={domaines}
          onUpdate={fields => onUpdateModule(module.id, fields)}
          onDelete={() => onDeleteModule(module.id)}
          onAddBelow={() => onAddModule(idx + 1)}
          onAddActivite={atIndex => onAddActivite(module.id, atIndex)}
          onUpdateActivite={onUpdateActivite}
          onDeleteActivite={onDeleteActivite}
        />
      ))}

      {mods.length === 0 && (
        <tr className="bg-gray-50">
          <td colSpan={7} className="px-4 py-2 text-sm text-gray-400 text-center">
            <button onClick={() => onAddModule()} className="hover:text-blue-600">+ Ajouter un module</button>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Vue principale ────────────────────────────────────────────────────────────
function FormationProgramme() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [programme, setProgramme] = useState(null);
  const [domaines, setDomaines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState(null);
  const saveTimerRef = useRef(null);

  const notifySave = (status) => {
    setSaveStatus(status);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (status === 'saved' || status === 'error') {
      saveTimerRef.current = setTimeout(() => setSaveStatus(null), 2500);
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [data, domainesData] = await Promise.all([
        programmeAPI.get(id),
        domainesExpertiseAPI.getAll(),
      ]);
      setProgramme(data);
      setDomaines(Array.isArray(domainesData) ? domainesData : []);
    } catch {
      setError('Erreur lors du chargement du programme');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // ── Formations (ex-chapitres) ──────────────────────────────────────────────
  const handleAddChapitre = async () => {
    try {
      notifySave('saving');
      const chapitre = await programmeAPI.createChapitre(id, { titre: 'Nouvelle formation', ordre: programme?.chapitres?.length ?? 0 });
      chapitre.modules = [];
      setProgramme(p => ({ ...p, chapitres: [...(p.chapitres ?? []), chapitre] }));
      notifySave('saved');
    } catch { notifySave('error'); }
  };

  const handleUpdateChapitre = async (chapitreId, fields) => {
    try {
      notifySave('saving');
      const updated = await programmeAPI.updateChapitre(chapitreId, fields);
      setProgramme(p => ({
        ...p,
        chapitres: (p.chapitres ?? []).map(c => c.id === chapitreId ? { ...c, ...updated } : c)
      }));
      notifySave('saved');
    } catch { notifySave('error'); }
  };

  const handleDeleteChapitre = async (chapitreId) => {
    try {
      await programmeAPI.deleteChapitre(chapitreId);
      setProgramme(p => ({ ...p, chapitres: (p.chapitres ?? []).filter(c => c.id !== chapitreId) }));
    } catch { notifySave('error'); }
  };

  // ── Modules ────────────────────────────────────────────────────────────────
  const handleAddModule = async (chapitreId, atIndex) => {
    try {
      notifySave('saving');
      const chapitre = (programme?.chapitres ?? []).find(c => c.id === chapitreId);
      const ordre = atIndex != null ? atIndex : (chapitre?.modules?.length ?? 0);
      const module = await programmeAPI.createModule(chapitreId, { titre: 'Nouveau module', type: 'module', statut: 'a_faire', ordre });
      module.activites = [];
      setProgramme(p => ({
        ...p,
        chapitres: (p.chapitres ?? []).map(c => {
          if (c.id !== chapitreId) return c;
          const mods = [...(c.modules ?? [])];
          mods.splice(ordre, 0, module);
          return { ...c, modules: mods };
        })
      }));
      notifySave('saved');
    } catch { notifySave('error'); }
  };

  const handleUpdateModule = async (moduleId, fields) => {
    try {
      notifySave('saving');
      const updated = await programmeAPI.updateModule(moduleId, fields);
      setProgramme(p => ({
        ...p,
        chapitres: (p.chapitres ?? []).map(c => ({
          ...c,
          modules: (c.modules ?? []).map(m => m.id === moduleId ? { ...m, ...updated } : m)
        }))
      }));
      notifySave('saved');
    } catch { notifySave('error'); }
  };

  const handleDeleteModule = async (moduleId) => {
    try {
      await programmeAPI.deleteModule(moduleId);
      setProgramme(p => ({
        ...p,
        chapitres: (p.chapitres ?? []).map(c => ({
          ...c,
          modules: (c.modules ?? []).filter(m => m.id !== moduleId)
        }))
      }));
    } catch { notifySave('error'); }
  };

  // ── Activités ──────────────────────────────────────────────────────────────
  const handleAddActivite = async (moduleId, atIndex) => {
    try {
      notifySave('saving');
      const module = programme?.chapitres?.flatMap(c => c.modules ?? []).find(m => m.id === moduleId);
      const ordre = atIndex ?? (module?.activites?.length ?? 0);
      const num = (module?.activites?.length ?? 0) + 1;
      const activite = await programmeAPI.createActivite(moduleId, { titre: `Activité ${num}`, type: 'activite', statut: 'a_faire', ordre });
      setProgramme(p => ({
        ...p,
        chapitres: p.chapitres.map(c => ({
          ...c,
          modules: c.modules.map(m => {
            if (m.id !== moduleId) return m;
            const acts = [...(m.activites ?? [])];
            acts.splice(ordre, 0, activite);
            return { ...m, activites: acts };
          })
        }))
      }));
      notifySave('saved');
    } catch { notifySave('error'); }
  };

  const handleUpdateActivite = async (activiteId, fields) => {
    try {
      notifySave('saving');
      const updated = await programmeAPI.updateActivite(activiteId, fields);
      setProgramme(p => ({
        ...p,
        chapitres: p.chapitres.map(c => ({
          ...c,
          modules: c.modules.map(m => ({
            ...m,
            activites: (m.activites ?? []).map(a => a.id === activiteId ? { ...a, ...updated } : a)
          }))
        }))
      }));
      notifySave('saved');
    } catch { notifySave('error'); }
  };

  const handleDeleteActivite = async (activiteId) => {
    try {
      await programmeAPI.deleteActivite(activiteId);
      setProgramme(p => ({
        ...p,
        chapitres: p.chapitres.map(c => ({
          ...c,
          modules: c.modules.map(m => ({
            ...m,
            activites: (m.activites ?? []).filter(a => a.id !== activiteId)
          }))
        }))
      }));
    } catch { notifySave('error'); }
  };

  // ── Stats ──────────────────────────────────────────────────────────────────
  const totalMinutes = programme?.chapitres?.reduce((sum, c) =>
    sum + (c.modules?.reduce((s, m) => {
      const acts = m.activites ?? [];
      const actTotal = acts.reduce((a, act) => a + (act.duree_minutes || 0), 0);
      return s + (acts.length > 0 ? actTotal : (m.duree_minutes || 0));
    }, 0) ?? 0), 0) ?? 0;

  const formatDuree = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}h`;
    return `${h}h${String(m).padStart(2, '0')}`;
  };

  const countByStatut = programme?.chapitres?.reduce((acc, c) => {
    c.modules?.forEach(m => { acc[m.statut] = (acc[m.statut] || 0) + 1; });
    return acc;
  }, {}) ?? {};
  const totalModules = Object.values(countByStatut).reduce((s, n) => s + n, 0);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="px-4 py-6 sm:px-0 flex justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
    </div>
  );

  if (error) return (
    <div className="px-4 py-6 sm:px-0">
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-600">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* En-tête */}
      <div className="bg-white shadow rounded-lg p-4 mb-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/formations')} className="text-gray-400 hover:text-gray-600 text-sm">← Formations</button>
          <h2 className="text-xl font-bold text-gray-900">{programme?.nom}</h2>
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-600">
          {saveStatus === 'saving' && <span className="text-blue-500 text-xs">Sauvegarde...</span>}
          {saveStatus === 'saved'  && <span className="text-green-600 text-xs">✓ Sauvegardé</span>}
          {saveStatus === 'error'  && <span className="text-red-600 text-xs">Erreur de sauvegarde</span>}
          {totalMinutes > 0 && <span className="font-medium text-blue-700">⏱ {formatDuree(totalMinutes)}</span>}
          {totalModules > 0 && (
            <>
              {countByStatut.fait     > 0 && <span className="text-green-600 font-medium">✓ {countByStatut.fait} fait</span>}
              {countByStatut.en_cours > 0 && <span className="text-orange-600 font-medium">⏵ {countByStatut.en_cours} en cours</span>}
              {countByStatut.a_faire  > 0 && <span className="text-gray-500">{countByStatut.a_faire} à faire</span>}
            </>
          )}
          <button
            onClick={handleAddChapitre}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 px-4 rounded-md text-sm transition"
          >
            + Formation
          </button>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white shadow rounded-lg overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wide">
              <th className="w-16 px-1 text-left py-2">Type</th>
              <th className="px-2 text-left py-2">Titre</th>
              <th className="w-20 px-2 text-right py-2">Durée (min)</th>
              <th className="w-24 px-2 text-center py-2">Statut</th>
              <th className="w-32 px-2 text-left py-2">Formateur</th>
              <th className="px-2 text-left py-2">Commentaire</th>
              <th className="w-16" />
            </tr>
          </thead>
          <tbody>
            {programme?.chapitres?.map(chapitre => (
              <FormationSection
                key={chapitre.id}
                chapitre={chapitre}
                domaines={domaines}
                onUpdateChapitre={fields => handleUpdateChapitre(chapitre.id, fields)}
                onDeleteChapitre={() => handleDeleteChapitre(chapitre.id)}
                onAddModule={idx => handleAddModule(chapitre.id, idx)}
                onUpdateModule={handleUpdateModule}
                onDeleteModule={handleDeleteModule}
                onAddActivite={handleAddActivite}
                onUpdateActivite={handleUpdateActivite}
                onDeleteActivite={handleDeleteActivite}
              />
            ))}
          </tbody>

          {totalMinutes > 0 && (
            <tfoot>
              <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold text-gray-700">
                <td colSpan={2} className="px-4 py-2 text-right">Total</td>
                <td className="px-2 py-2 text-right">{totalMinutes} min</td>
                <td className="px-2 py-2 text-center text-gray-500 text-xs">{formatDuree(totalMinutes)}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>

        {(programme?.chapitres?.length === 0) && (
          <div className="text-center py-16">
            <p className="text-gray-400 mb-4">Ce programme est vide</p>
            <button onClick={handleAddChapitre} className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition">
              + Ajouter une première formation
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default FormationProgramme;
